// Your portfolio data (P1.10, owner 4 Nov): a person can always take her own
// Brand Ambassador data down — leave the catalogue, withdraw a submission,
// delete a photo, make a profile link private — with or without the Brand
// Ambassador role, and while BA is switched off. Deliberately behind NO BA
// gate: employee-service never gates these (WP-2c). Nothing here creates,
// edits, submits or publishes; that stays on My Portfolio.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldCheck, Trash2, Undo2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getMyBaPrivacy, privacyDeleteImage, privacyMakeLinkPrivate, privacyUnpublish, privacyWithdraw,
  type BaPrivacyView,
} from '@/services/baPrivacyService';
import { BA_REVOCATION_TEXT } from '../portfolio';

export default function BaPrivacyPage() {
  const { t } = useTranslation('brandAmbassadors');
  const confirm = useConfirm();
  const [data, setData] = useState<BaPrivacyView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    getMyBaPrivacy().then(setData).catch((e) => {
      setError(getApiErrorMessage(e, t('errors.load', { defaultValue: 'Could not load your portfolio.' }) as string));
    });
  }, [t]);
  useEffect(load, [load]);

  /** Ask, do it, read the view again (the actions answer in other shapes). */
  const act = async (ask: { title: string; message: string; confirmLabel: string; danger?: boolean },
    fn: () => Promise<void>, ok: string) => {
    if (!(await confirm(ask))) return;
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.action', { defaultValue: 'That didn’t work. Try again.' }) as string));
    } finally {
      setBusy(false);
      load();
    }
  };

  const title = t('privacy.title', { defaultValue: 'Your portfolio data' });
  if (error) return <DataState variant="error" message={error} onRetry={load} />;
  if (data === undefined) return <DataState variant="loading" />;
  if (data === null) {
    return <DataState variant="empty" title={title as string}
      message={t('privacy.none', { defaultValue: 'You have no Brand Ambassador portfolio data.' }) as string} />;
  }

  const live = data.publication.status === 'published';
  const images = data.media.filter((m) => !m.removed);
  const s = (k: string, d: string, vars?: Record<string, unknown>) => t(k, { defaultValue: d, ...vars }) as string;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
          <ShieldCheck className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-charcoal">{title}</h1>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {s('privacy.intro', 'You can always take your Brand Ambassador portfolio down, even without the Brand Ambassador role. Creating or editing a portfolio needs the role.')}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 pt-4 space-y-3">
          <p className="text-sm font-medium" data-testid="ba-privacy-status">
            {live ? s('privacy.live', 'Clients can see your portfolio.') : s('privacy.not_live', 'Clients can’t see your portfolio.')}
          </p>
          {data.submitted_revision && (
            <p className="text-sm">{s('privacy.under_review', 'Version {{n}} is with your company for review.', { n: data.submitted_revision.revision_no })}</p>
          )}
          {(data.submitted_revision || live) && <div className="flex flex-wrap gap-2">
            {data.submitted_revision && (
              <Button variant="outline" disabled={busy} onClick={() => act({
                title: s('privacy.withdraw_title', 'Withdraw your submission?'),
                message: s('privacy.withdraw_message', 'Your company stops reviewing it. What clients see now doesn’t change.'),
                confirmLabel: s('actions.withdraw', 'Withdraw submission'),
              }, privacyWithdraw, s('privacy.toast_withdrawn', 'Submission withdrawn.'))}>
                <Undo2 className="w-4 h-4 mr-1" />{s('actions.withdraw', 'Withdraw submission')}
              </Button>
            )}
            {live && (
              <Button variant="outline" className="text-danger" disabled={busy} onClick={() => act({
                title: s('remove.title', 'Remove me from the catalogue'),
                message: s('remove.message', `${BA_REVOCATION_TEXT} Your assignment history is kept.`),
                confirmLabel: s('remove.confirm', 'Remove me'),
                danger: true,
              }, privacyUnpublish, s('toast.removed', 'You’re no longer in the catalogue.'))}>
                {s('actions.remove', 'Remove me from the catalogue')}
              </Button>
            )}
          </div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{s('privacy.images', 'Your photos')}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {images.length === 0 && <p className="text-sm text-muted-foreground">{s('privacy.no_images', 'No photos.')}</p>}
          {images.map((m, i) => (
            <div key={m.id} className="flex items-center justify-between gap-3">
              <span className="text-sm">{m.caption || s('privacy.photo_n', 'Photo {{n}}', { n: i + 1 })}</span>
              <Button size="sm" variant="outline" className="text-danger" disabled={busy}
                aria-label={`${s('privacy.delete_photo', 'Delete photo')}: ${m.caption || i + 1}`}
                onClick={() => act({
                  title: s('privacy.delete_photo', 'Delete photo'),
                  message: s('privacy.delete_photo_confirm', 'Delete this photo? If clients can see it, it leaves your live profile at once.'),
                  confirmLabel: s('privacy.delete_photo', 'Delete photo'),
                  danger: true,
                }, () => privacyDeleteImage(m.id), s('privacy.toast_image', 'Photo deleted.'))}>
                <Trash2 className="w-4 h-4 mr-1" />{s('privacy.delete_photo', 'Delete photo')}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {data.social_links.length > 0 && (
        <Card>
          <CardHeader><CardTitle>{s('privacy.links', 'Profile links shown in your portfolio')}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.social_links.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3">
                <span className="text-sm capitalize">{l.platform}</span>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => act({
                  title: s('privacy.link_private_title', 'Make this link private?'),
                  message: s('privacy.link_private_message', 'It leaves your portfolio at once.'),
                  confirmLabel: s('privacy.make_private', 'Make private'),
                }, () => privacyMakeLinkPrivate(l.id), s('privacy.toast_link', 'Link made private.'))}>
                  {s('privacy.make_private', 'Make private')}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** The entry on the Privacy & Trust Center: shown only when she has BA
 *  portfolio data (any non-200 hides it), with or without the role. */
export function BaPrivacyEntry() {
  const { t } = useTranslation('brandAmbassadors');
  const [has, setHas] = useState(false);
  useEffect(() => {
    let live = true;
    getMyBaPrivacy().then((v) => { if (live) setHas(!!v); }).catch(() => { if (live) setHas(false); });
    return () => { live = false; };
  }, []);
  if (!has) return null;
  return (
    <Card>
      <CardContent className="p-4 pt-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{t('privacy.entry', { defaultValue: 'Brand Ambassador portfolio data' })}</p>
          <p className="text-xs text-muted-foreground">
            {t('privacy.entry_hint', { defaultValue: 'See it, and take it down at any time.' })}
          </p>
        </div>
        {/* Relative to the Privacy & Trust Center route: /<workspace>/privacy/ba-portfolio. */}
        <Link to="ba-portfolio" data-testid="ba-privacy-entry"
          className="text-sm font-medium text-brand hover:underline whitespace-nowrap">
          {t('privacy.open', { defaultValue: 'Open' })}
        </Link>
      </CardContent>
    </Card>
  );
}
